import { createMemo, createSignal, For, Show } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import { Pencil, Plus, School, Trash2 } from "@fleet/icons";

import { Button } from "@/components/ui/button";
import { Badge, Card, CardContent, Label, TextField } from "@fleet/ui";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import { createPowerSyncQuery } from "@custodia/shared/solid";
import {
  type AulaRecord,
  createAulaMutation,
  deleteAulaMutation,
  getAulasQuery,
  getLocalOrganizationsQuery,
  groupAulasByOrg,
  type OrganizationRecord,
  queryKeys,
  type SupervisedProfileRecord,
  updateAulaMutation,
} from "@custodia/shared";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

interface AulasPanelProps {
  profiles: readonly SupervisedProfileRecord[];
  /** Active aula filter (id | "none" | null = no filter). */
  aulaFilter: string | null;
  onAulaFilterChange: (filter: string | null) => void;
}

/**
 * Classrooms panel (school multi-org): grouping of supervised profiles
 * inside an organization. Only displayed if user belongs to an organization —
 * a family without school never sees it.
 */
export function AulasPanel(props: AulasPanelProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;

  const [dialogOpen, setDialogOpen] = createSignal(false);
  const [editing, setEditing] = createSignal<AulaRecord | null>(null);
  const [name, setName] = createSignal("");
  const [orgId, setOrgId] = createSignal("");
  const [isSubmitting, setIsSubmitting] = createSignal(false);

  const orgs = createPowerSyncQuery<OrganizationRecord>({
    queryKey: () => queryKeys.organizations.localList(),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getLocalOrganizationsQuery(dbInstance) : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  const aulas = createPowerSyncQuery<AulaRecord>({
    queryKey: () => queryKeys.supervision.aulas(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getAulasQuery(dbInstance) : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  const orgName = (id: string) => orgs.data?.find((o) => o.id === id)?.name ?? id;

  const grouped = createMemo(() => groupAulasByOrg(aulas.data ?? [], props.profiles));

  const openNew = () => {
    setEditing(null);
    setName("");
    setOrgId(orgs.data?.[0]?.id ?? "");
    setDialogOpen(true);
  };

  const openEdit = (record: AulaRecord) => {
    setEditing(record);
    setName(record.name ?? "");
    setOrgId(record.organization_id ?? "");
    setDialogOpen(true);
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    const dbInstance = drizzle();
    const uid = userId();
    const editingAula = editing();
    if (!name().trim() || !dbInstance || !uid || (!editingAula && !orgId())) {
      return;
    }
    setIsSubmitting(true);
    try {
      if (editingAula) {
        await updateAulaMutation(
          dbInstance,
          editingAula.id,
          { name: name().trim() },
          editingAula.organization_id,
        );
      } else {
        await createAulaMutation(dbInstance, {
          userId: uid,
          organizationId: orgId(),
          name: name().trim(),
        });
      }
      setDialogOpen(false);
      showToast(editingAula ? t(msg`Classroom saved`) : t(msg`Classroom created`), "success");
    } catch (err) {
      console.error("Error saving classroom:", err);
      showToast(t(msg`Could not save the classroom. Try again.`), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (record: AulaRecord) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await deleteAulaMutation(dbInstance, record.id);
      if (props.aulaFilter === record.id) {
        props.onAulaFilterChange(null);
      }
      showToast(t(msg`Classroom removed — its profiles were kept`), "success");
    } catch (err) {
      console.error("Error deleting classroom:", err);
      showToast(t(msg`Could not remove the classroom. Try again.`), "error");
    }
  };

  // Without orgs no classrooms are possible: the entire panel disappears (pure family).
  const hasOrgs = () => (orgs.data?.length ?? 0) > 0;

  return (
    <Show when={hasOrgs()}>
      <section class="mb-6" data-testid="aulas-panel">
        <div class="flex items-center justify-between gap-4 mb-3">
          <h2 class="flex items-center gap-2 text-lg font-semibold">
            <School class="h-5 w-5 text-primary" />
            {t(msg`Classrooms`)}
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={openNew}
            class="gap-1.5"
            data-testid="new-aula"
          >
            <Plus class="h-4 w-4" />
            {t(msg`New classroom`)}
          </Button>
        </div>

        <Show
          when={(aulas.data?.length ?? 0) > 0}
          fallback={
            <p class="text-sm text-muted-foreground" data-testid="aulas-empty">
              {t(msg`Group the profiles of your school by classroom to organize and filter them.`)}
            </p>
          }
        >
          <div class="space-y-3">
            <For each={[...grouped().entries()]}>
              {([groupOrgId, bucket]) => (
                <div>
                  <Show when={grouped().size > 1}>
                    <p class="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {orgName(groupOrgId)}
                    </p>
                  </Show>
                  <div class="flex flex-wrap gap-2" data-testid="aulas-list">
                    <For each={bucket}>
                      {({ aula: record, profileCount }) => (
                        <Card
                          class={
                            props.aulaFilter === record.id
                              ? "border-primary/60 bg-primary/5"
                              : undefined
                          }
                        >
                          <CardContent class="flex items-center gap-2 p-2 pl-3">
                            <button
                              type="button"
                              class="flex items-center gap-2 text-left"
                              data-testid="aula-chip"
                              aria-pressed={props.aulaFilter === record.id}
                              onClick={() =>
                                props.onAulaFilterChange(
                                  props.aulaFilter === record.id ? null : record.id,
                                )
                              }
                            >
                              <span class="font-medium">{record.name}</span>
                              <Badge variant="secondary" data-testid="aula-count">
                                {profileCount}
                              </Badge>
                            </button>
                            <Button
                              variant="ghost"
                              size="icon"
                              class="h-7 w-7"
                              aria-label={t(msg`Edit classroom`)}
                              onClick={() => openEdit(record)}
                            >
                              <Pencil class="h-3.5 w-3.5" />
                            </Button>
                            <ConfirmDialog
                              title={t(msg`Remove classroom?`)}
                              description={t(
                                msg`Profiles assigned to it are kept and simply unassigned.`,
                              )}
                              confirmLabel={t(msg`Remove`)}
                              variant="destructive"
                              onConfirm={() => void handleDelete(record)}
                              renderTrigger={(open) => (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  class="h-7 w-7 text-destructive hover:bg-destructive/10"
                                  aria-label={t(msg`Remove classroom`)}
                                  data-testid="delete-aula"
                                  onClick={open}
                                >
                                  <Trash2 class="h-3.5 w-3.5" />
                                </Button>
                              )}
                            />
                          </CardContent>
                        </Card>
                      )}
                    </For>
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>

        <Dialog open={dialogOpen()} onOpenChange={setDialogOpen} contentClass="max-w-md p-0">
          <DialogHeader size="md">
            <DialogTitle class="text-xl font-bold">
              {editing() ? t(msg`Edit classroom`) : t(msg`New classroom`)}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => void handleSubmit(e)} class="space-y-4 p-6">
            <div class="space-y-2">
              <Label for="aula-name">{t(msg`Classroom name`)}</Label>
              <TextField
                id="aula-name"
                value={name()}
                onInput={(e) => setName(e.currentTarget.value)}
                placeholder={t(msg`e.g. Grade 3B`)}
                autofocus
              />
            </div>
            <Show when={!editing()}>
              <div class="space-y-2">
                <Label for="aula-org">{t(msg`School / organization`)}</Label>
                <Select
                  id="aula-org"
                  value={orgId()}
                  onChange={setOrgId}
                  options={(orgs.data ?? []).map((o) => ({
                    value: o.id,
                    label: o.name ?? o.id,
                  }))}
                  placeholder={t(msg`Choose an organization`)}
                  data-testid="aula-org-select"
                />
              </div>
            </Show>
            <div class="flex items-center justify-end gap-3 pt-4 border-t border-border/10">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                {t(msg`Cancel`)}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting() || !name().trim() || (!editing() && !orgId())}
                data-testid="save-aula"
                class="disabled:cursor-not-allowed disabled:opacity-50"
              >
                {editing() ? t(msg`Save changes`) : t(msg`Create classroom`)}
              </Button>
            </div>
          </form>
        </Dialog>
      </section>
    </Show>
  );
}
